import { useEffect, useRef, useState } from "react";

/**
 * Per-peer media diagnostics from RTCPeerConnection stats: is the connection up, is THEIR audio reaching
 * us (bytes arriving), is OURS leaving (bytes sent), and how loud they are. This is what answers "whose
 * audio is flowing?" — the one-way-audio case where a tile looks fine but nothing is heard.
 */
export interface PeerHealth {
    state: RTCPeerConnectionState;
    ice: RTCIceConnectionState;
    /** Their audio packets are arriving. */
    receivingAudio: boolean;
    /** Our audio packets are going out to them. */
    sendingAudio: boolean;
    /** Their current input level, 0–1 (from the inbound stats). */
    audioLevel: number;
    /** When we last saw their audio bytes grow (ms epoch), 0 if never. */
    lastAudioAt: number;
}

interface Counters {
    rx: number;
    tx: number;
    lastAudioAt: number;
}

export function usePeerHealth(getPeers: () => Record<string, RTCPeerConnection>, intervalMs = 1500): Record<string, PeerHealth> {
    const [health, setHealth] = useState<Record<string, PeerHealth>>({});
    const getRef = useRef(getPeers);
    getRef.current = getPeers;
    const counters = useRef(new WeakMap<RTCPeerConnection, Counters>());

    useEffect(() => {
        let cancelled = false;
        const tick = async () => {
            const peers = getRef.current();
            const next: Record<string, PeerHealth> = {};
            await Promise.all(Object.entries(peers).map(async ([id, pc]) => {
                const prev = counters.current.get(pc) ?? { rx: 0, tx: 0, lastAudioAt: 0 };
                let rx = 0;
                let tx = 0;
                let level = 0;
                if (pc.connectionState !== "closed") {
                    try {
                        const report = await pc.getStats();
                        report.forEach((s: RTCStats & Record<string, unknown>) => {
                            const kind = (s["kind"] ?? s["mediaType"]) as string | undefined;
                            if (kind !== "audio") return;
                            if (s.type === "inbound-rtp") {
                                rx += typeof s["bytesReceived"] === "number" ? s["bytesReceived"] as number : 0;
                                if (typeof s["audioLevel"] === "number") level = Math.max(level, s["audioLevel"] as number);
                            } else if (s.type === "outbound-rtp") {
                                tx += typeof s["bytesSent"] === "number" ? s["bytesSent"] as number : 0;
                            }
                        });
                    } catch { /* closing */ }
                }
                const now = Date.now();
                const receiving = rx > prev.rx;
                const sending = tx > prev.tx;
                const c: Counters = { rx, tx, lastAudioAt: receiving ? now : prev.lastAudioAt };
                counters.current.set(pc, c);
                next[id] = {
                    state: pc.connectionState,
                    ice: pc.iceConnectionState,
                    receivingAudio: receiving,
                    sendingAudio: sending,
                    audioLevel: level,
                    lastAudioAt: c.lastAudioAt
                };
            }));
            if (!cancelled) setHealth(next);
        };
        void tick();
        const handle = window.setInterval(() => void tick(), intervalMs);
        return () => {
            cancelled = true;
            window.clearInterval(handle);
        };
    }, [intervalMs]);

    return health;
}
